import { COLORS } from '@btime/constants'
import React, { useCallback, useMemo } from 'react'

import { Table, TableRow } from '../../../../../components/pdf/Table'
import { TableColText, TableHeadText } from '../../../../../components/pdf/TableHelpers'
import Gallery from '../../../../../components/pdf/Gallery'
import Image from '../../../../../components/pdf/Image'
import PointsTableWithImage from '../../_shared/PointsTableWithImage'
import Separator from '../../../../../components/pdf/Separator'
import Text, { Bold } from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'

import {
  remapReplies,
  getImages,
  findReplyByQuestionName,
  remapReplyChildren,
  getSeatsReplies,
  getTablesReplies,
  filterRepliesByQuestionName,
  findReplyByReference,
  getJobDescription
} from '../../_helpers/pdfHelpers'

const FurnitureSection = ({ serviceOrder }) => {
  const remap = useMemo(() => remapReplyChildren(serviceOrder.replies), [serviceOrder.replies])

  const jobRoles = useMemo(() => {
    const replies = filterRepliesByQuestionName(serviceOrder.replies, 'Quantos funcionários possui nesse cargo?')
    const result = replies.map(reply => {
      return findReplyByReference(serviceOrder.replies, reply.question.parentReference)
    })
    const jobs = result.map(reply => {
      return getJobDescription(reply.question.name).funcao
    })
    return jobs
  }, [serviceOrder.replies])

  const data = useMemo(() => {
    const result = remapReplies(serviceOrder.replies, remap)
    return result
  }, [remap, serviceOrder.replies])

  const respostasDeAssentos = useCallback(
    resposta => {
      return getSeatsReplies(resposta, remap)
    },
    [remap]
  )

  const respostasDeMesas = useCallback(
    resposta => {
      return getTablesReplies(resposta, remap)
    },
    [remap]
  )

  const monitoresNotebooks = getImages(findReplyByQuestionName(serviceOrder.replies, 'MONITORES E NOTEBOOKS'), 0)

  const imagemTeclado = getImages(findReplyByQuestionName(serviceOrder.replies, 'TECLADOS'), 0)

  const imagemMouse = getImages(findReplyByQuestionName(serviceOrder.replies, 'Mouse'), 0)

  const imagemOrganizacao = getImages(findReplyByQuestionName(serviceOrder.replies, 'ORGANIZAÇÃO DO TRABALHO'), 0)

  return (
    <View id="11" pageBreakAfter>
      <View pageBreakAfter>
        <Text bold size="small">
          11. MOBILIÁRIO DOS POSTOS DE TRABALHO
        </Text>

        <Separator weight={8} />

        <Text bold size="small" id={'11.1'}>
          11.1. ASSENTOS
        </Text>

        <Separator weight={4} />

        <Text>
          Neste tópico serão avaliados os assentos utilizados na unidade. O trabalho sentado parece confortável
          comparado com outras formas de trabalhar. No entanto, ficar sentado ao longo de muitas horas desgasta do mesmo
          modo. Uma boa cadeira reduz o cansaço, melhora a eficiência do trabalho e aumenta a satisfação no emprego.
          <br />
          Os critérios de avaliação consideram a pontuação indicada abaixo da foto registrada.
        </Text>

        {data.atendimento &&
          data.atendimento.map((resposta, index) => {
            const qualMobiliario = findReplyByQuestionName(resposta.childrenReplies, 'Qual mobiliário?')
            const respostasDoMobiliario = remap[qualMobiliario.question.reference]
            const reply = findReplyByQuestionName(respostasDoMobiliario, 'FOTO CADEIRA (FRENTE) TIRAR 2 FOTOS')
            const image = getImages(reply, 0)
            const sections = respostasDeAssentos(resposta)

            return (
              <View noWrap key={index}>
                <Text size="small">
                  <Bold size="small">SETOR:</Bold> ATENDIMENTO
                </Text>

                <Text size="small">
                  <Bold size="small">CARGO:</Bold> {resposta.funcao}
                </Text>
                <PointsTableWithImage
                  furnitureType={qualMobiliario.value.map(e => e.name).join(' ')}
                  setor={'ATENDIMENTO'}
                  tipo="assentos"
                  title="Tipo de Assento"
                  image={image}
                  sections={sections}
                />
              </View>
            )
          })}

        {data.comercial &&
          data.comercial.map((resposta, index) => {
            const qualMobiliario = findReplyByQuestionName(resposta.childrenReplies, 'Qual mobiliário?')
            const respostasDoMobiliario = remap[qualMobiliario.question.reference]
            const reply = findReplyByQuestionName(respostasDoMobiliario, 'FOTO CADEIRA (FRENTE) TIRAR 2 FOTOS')
            const image = getImages(reply, 0)
            const sections = respostasDeAssentos(resposta)
            return (
              <View noWrap key={index}>
                <Text size="small">
                  <Bold size="small">SETOR:</Bold> COMERCIAL
                </Text>

                <Text size="small">
                  <Bold size="small">CARGO:</Bold> {resposta.funcao}
                </Text>
                <PointsTableWithImage
                  furnitureType={qualMobiliario.value.map(e => e.name).join(' ')}
                  setor={'COMERCIAL'}
                  title="Tipo de Assento"
                  tipo="assentos"
                  image={image}
                  sections={sections}
                />
              </View>
            )
          })}

        <Separator weight={8} />

        <Text bold size="small" id={'11.2'}>
          11.2. MESAS DE TRABALHO
        </Text>

        <Separator weight={4} />

        <Text>
          Neste tópico serão avaliadas as mesas utilizadas na unidade. Serão considerados os aspectos legais,
          características físicas e organização do espaço.
          <br />
          Os critérios de avaliação consideram a pontuação indicada abaixo da foto registrada.
        </Text>

        <Separator weight={8} />

        {data.atendimento &&
          data.atendimento.map((resposta, index) => {
            const qualMobiliario = findReplyByQuestionName(resposta.childrenReplies, 'Qual mobiliário?')
            const respostasDoMobiliario = remap[qualMobiliario.question.reference]
            const reply = findReplyByQuestionName(
              respostasDoMobiliario,
              'FOTO MESA GERAL (MESA TECLADO, MONITOR E MOUSE) TIRAR 2 FOTOS'
            )
            const image = getImages(reply, 0)
            const sections = respostasDeMesas(resposta)

            return (
              <View noWrap key={index}>
                <Text size="small">
                  <Bold size="small">SETOR:</Bold> ATENDIMENTO
                </Text>

                <Text size="small">
                  <Bold size="small">CARGO:</Bold> {resposta.funcao}
                </Text>

                <PointsTableWithImage
                  furnitureType={qualMobiliario.value.map(e => e.name).join(' ')}
                  setor={'ATENDIMENTO'}
                  tipo="mesas"
                  title="Tipo de Mesa"
                  image={image}
                  sections={sections}
                />
              </View>
            )
          })}

        {data.comercial &&
          data.comercial.map((resposta, index) => {
            const qualMobiliario = findReplyByQuestionName(resposta.childrenReplies, 'Qual mobiliário?')
            const respostasDoMobiliario = remap[qualMobiliario.question.reference]
            const reply = findReplyByQuestionName(
              respostasDoMobiliario,
              'FOTO MESA GERAL (MESA TECLADO, MONITOR E MOUSE) TIRAR 2 FOTOS'
            )
            const image = getImages(reply, 0)
            const sections = respostasDeMesas(resposta)
            return (
              <View noWrap key={index}>
                <Text size="small">
                  <Bold size="small">SETOR:</Bold> COMERCIAL
                </Text>

                <Text size="small">
                  <Bold size="small">CARGO:</Bold> {resposta.funcao}
                </Text>

                <PointsTableWithImage
                  furnitureType={qualMobiliario.value.map(e => e.name).join(' ')}
                  setor={'COMERCIAL'}
                  title="Tipo de Mesa"
                  tipo="mesas"
                  image={image}
                  sections={sections}
                />
              </View>
            )
          })}
      </View>

      <Text>
        O ajuste sobre uma mesa de trabalho deve considerar o conjunto assento e monitor. Dimensões adequadas para a
        aquisição (caso ocorra) de novos mobiliários (mesas) Tabela 1 – Conforme ABNT NBR 13.966/08
      </Text>

      <Separator weight={8} />

      <Table width="100%">
        <TableRow>
          <TableHeadText rowSpan={2} align="center">
            Código
          </TableHeadText>
          <TableHeadText rowSpan={2} align="center">
            Nome da variável
          </TableHeadText>
          <TableHeadText align="center" colSpan={2}>
            Valor
          </TableHeadText>
        </TableRow>

        <TableRow>
          <TableHeadText align="center">Máximo</TableHeadText>
          <TableHeadText align="center">Mínimo</TableHeadText>
        </TableRow>

        <TableRow>
          <TableColText align="center">I1</TableColText>
          <TableColText>Largura da mesa de trabalho</TableColText>
          <TableColText align="center">800 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">I2</TableColText>
          <TableColText>Largura da mesa de reunião</TableColText>
          <TableColText align="center">1000 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">I3</TableColText>
          <TableColText>Largura das pernas</TableColText>
          <TableColText align="center">600 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">P1</TableColText>
          <TableColText>Profundidade da mesa de trabalho</TableColText>
          <TableColText align="center">600 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">P2</TableColText>
          <TableColText>Profundidade da mesa de reunião</TableColText>
          <TableColText align="center">800 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">P3</TableColText>
          <TableColText>Profundidade da mesa ou conexão utilizada com microcomputador</TableColText>
          <TableColText align="center">750 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">P4</TableColText>
          <TableColText>Profundidade livre para joelhos</TableColText>
          <TableColText align="center">450 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">P5</TableColText>
          <TableColText>Profundidade livre para os pés</TableColText>
          <TableColText align="center">570 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">P6</TableColText>
          <TableColText>Profundidade livre para as coxas</TableColText>
          <TableColText align="center">200 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">H1</TableColText>
          <TableColText>Altura da mesa de trabalho e de reunião *</TableColText>
          <TableColText align="center">720 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">H2</TableColText>
          <TableColText>Altura livre sobre o tampo</TableColText>
          <TableColText align="center">660 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">H3</TableColText>
          <TableColText>Altura livre para as coxas</TableColText>
          <TableColText align="center">620 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">H4</TableColText>
          <TableColText>Altura livre para os joelhos</TableColText>
          <TableColText align="center">550 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">H5</TableColText>
          <TableColText>Altura livre para os pés</TableColText>
          <TableColText align="center">120 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">D</TableColText>
          <TableColText>Diâmetro da mesa</TableColText>
          <TableColText align="center">800 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>

        <TableRow>
          <TableColText align="center">R</TableColText>
          <TableColText>Raio da borda de contato com o usuário</TableColText>
          <TableColText align="center">2,5 mm</TableColText>
          <TableColText align="center">-</TableColText>
        </TableRow>
      </Table>

      <Separator weight={8} />

      <Text bold size="small" id={'11.3'}>
        11.3. MONITORES/ NOTEBOOKS
      </Text>

      <Separator weight={4} />

      <Text>
        O estudo do conjunto viabiliza avaliar a compatibilização dos mobiliários de acordo com o perfil antropométrico
        da empresa. Para isso foram verificadas as regulagens de assentos, mesas, ajustes de monitores e seus suportes.
      </Text>

      <Separator weight={4} />

      <Gallery items={monitoresNotebooks?.length ? [{ src: monitoresNotebooks[0] }] : []} />

      <Separator weight={4} />

      <Text bold size="small">
        CONCLUSÃO / AÇÃO DE ERGONOMIA
      </Text>

      <ul>
        <li>
          <Text underlineColor={COLORS.lightGreen}>
            Os monitores OU NOTEBOOKS atendem a Norma Regulamentadora n°17, item 17.7.3.2
          </Text>
        </li>
      </ul>

      <Separator weight={8} />

      <Text bold size="small" id={'11.4'}>
        11.4. TECLADOS
      </Text>

      <Separator weight={4} />

      <Text>
        Serão avaliados os teclados (amostragem) dos computadores usados nas instalações da empresa.
        <br />
        Os critérios de avaliação consideram a pontuação indicada abaixo da foto registrada.
      </Text>

      <Separator weight={8} />

      <PointsTableWithImage
        tipo="teclado"
        title="Teclado de computador tipo CPU"
        image={imagemTeclado}
        sections={[
          {
            label: 'A. Requisitos Mínimos Legais',
            rows: [
              {
                label: 'É fino',
                value: true
              },
              {
                label: 'É macio',
                value: true
              },
              {
                label: 'É independente do monitor permitindo ajustes',
                value: true
              },

              {
                label: 'As teclas possuem dimensões corretas',
                value: true
              },
              {
                label: 'Apresenta tipo de formato tradicional e simplificado que facilita sua utilização',
                value: true
              },
              {
                label: 'As teclas se encontram legíveis',
                value: true
              },
              {
                label: 'As teclas estão em perfeitas condições de uso',
                value: true
              },
              {
                label: 'Existe teclado numérico separado em região específica do teclado',
                value: true
              }
            ]
          }
        ]}
      />

      <Separator weight={8} />

      <Text>
        Para uma melhor posição dos punhos no momento da digitação, recomendamos o fornecimento do apoio ergonômico de
        punho/pulso como base no teclado ou não, conforme identificado um em um posto de trabalho, onde o colaborador,
        além de prevenção nas lesões futuras, esse apoio oferece uma agradável sensação de descanso. Deverá ser
        posicionado em frente ao teclado para possibilitar o apoio dos punhos, como mostra ilustração a seguir:
      </Text>

      <Separator weight={8} />

      <Text bold size="small" id={'11.5'}>
        11.5. MOUSES
      </Text>

      <Separator weight={4} />

      <Text>Os Mouses devem ser utilizados para o auxílio nas atividades.</Text>

      <Separator weight={4} />

      <Text size="small">
        <Bold size="small">SETOR:</Bold> ATENDIMENTO E COMERCIAL
      </Text>

      <Text size="small">
        <Bold size="small">CARGOS:</Bold> {jobRoles.join(' ')}
      </Text>

      <Separator weight={4} />

      <Text>Os critérios de avaliação consideram a pontuação indicada abaixo da foto registrada.</Text>

      <Separator weight={8} />
      <PointsTableWithImage
        tipo={'mouse'}
        title="Mouse"
        image={imagemMouse}
        sections={[
          {
            label: 'Requisitos Ergonômicos de Boas Práticas',
            rows: [
              {
                label: 'Possuem no mínimo 02 botões',
                value: true
              },
              {
                label: 'Possuem tamanho adequado, de forma que fica praticamente encaixado na palma da mão',
                value: true
              },
              {
                label: 'Estão posicionados na mesma altura do teclado',
                value: true
              },
              {
                label: 'Estão em condições adequadas de uso',
                value: true
              },
              {
                label: 'Os botões são macios não sendo necessária a aplicação de força excessiva',
                value: true
              },
              {
                label: 'A base onde são apoiados possui espaço suficiente para movimentação',
                value: true
              }
            ]
          }
        ]}
      />

      <Separator weight={8} />

      <Text size="small">
        <Bold size="small">Regular os controles de software - </Bold> Pode utilizar o painel de controle de software
        para regular as propriedades do dispositivo apontador.
        <br />
        <Bold size="small">Limpar frequentemente - </Bold> A sujeira pode dificultar o desempenho do dispositivo
        apontador. Lembre-se de limpar o mouse frequentemente.
      </Text>

      <Separator weight={8} />

      <Text bold size="small" id={'11.6'}>
        11.6. APOIO PARA OS PÉS
      </Text>

      <Separator weight={4} />

      <Text>
        O item 17.6.4 descreve da seguinte forma sobre a utilização de apoio para os pés, “Para adaptação do mobiliário
        às dimensões antropométricas do trabalhador, pode ser utilizado apoio para os pés sempre que o trabalhador não
        puder manter a planta dos pés completamente apoiada no piso”.
        <br />
        Acessório para apoio dos pés do usuário em altura e ângulo que favoreçam a postura adequada ao trabalho
        informatizado.
      </Text>

      <ul>
        <li>
          <Text>
            1. Demanda regular: Intranet – Orion Sap – Compras e realiza a busca “Apoio de pé” – Solicitação – Criar
            solicitação
          </Text>
        </li>

        <li>
          <Text>
            2. Para casos especiais. A solicitação deve ser feita através do SESMT – Segurança do Trabalho através da
            caixa jurídica -<a href="mailto:rhsegurancatrabalho@unimed.com.br">rhsegurancatrabalho@unimed.com.br</a>{' '}
            solicitação
          </Text>
        </li>

        <li>
          <Text>
            3. Apoio de pés – caixa alto. A demanda é através de ordem de serviço para a Engenharia (quem abre esta
            demanda também somos nós em conjunto com a área médica do Banco). Esta solicitação do Orion Sap também vale
            para apoio de punho e mouse pad. Já suportes de notebook, apoio lombar, são atendidos conforme fluxo do item
            2.
          </Text>
        </li>
      </ul>

      <Separator weight={4} />

      <Text size="small">
        <Bold size="small">SETOR:</Bold> ATENDIMENTO E COMERCIAL
      </Text>

      <Text size="small">
        <Bold size="small">CARGOS:</Bold> {jobRoles.join(' ')}
      </Text>

      <Separator weight={4} />

      <Text bold size="small">
        AVALIAÇÃO
      </Text>

      <Separator weight={2} />

      <Text>
        Em geral, qualquer local de trabalho há muitas diferenças nas dimensões corporais dos trabalhadores. Essas
        diferenças tendem a ser cada vez maiores, pois se apresentam juntos trabalhadores de ambos os sexos e de zonas
        geográficas diferentes. Pelo fato de existirem grande variação de estatura entre os colaboradores da empresa, e
        pelo fato de a mesa de trabalho não possuir regulagem de altura pode ocorrer desconforto para os colaboradores
        com estatura inferior a 1,65m.
      </Text>

      <Separator weight={8} />

      <Text bold size="small">
        CONCLUSÃO / AÇÃO DE ERGONOMIA
      </Text>

      <ul>
        <li>
          <Text underlineColor={COLORS.lightGreen}>
            A Unimed tem como prática fornecer apoios para os pés de acordo com as não conformidades encontradas
            mediante abertura de chamado dos funcionários.
          </Text>
        </li>
      </ul>

      <Separator weight={8} />

      <Text bold size="small" id={'11.7'}>
        11.7. ORGANIZAÇÃO DE TRABALHO
      </Text>

      <Separator weight={4} />

      <Text>
        “A organização do trabalho deve ser adequada às características psicofisiológicas dos trabalhadores e à natureza
        do trabalho a ser executado e deve-se levar em consideração, no mínimo: as normas de produção; o modo
        operatório; a exigência de tempo; a determinação do conteúdo de tempo; o ritmo de trabalho; e o conteúdo das
        tarefas”.
        <br />
        Situar os materiais e controles utilizados com maior frequência em uma área de fácil alcance. Liberar espaço na
        mesa de trabalho, de modo que haja uma boa mobilidade de movimentação dos materiais e objetos de uso diário.
        Tempo e energia são poupados situando os materiais, ferramentas e controles a uma distância confortável para o
        alcance dos trabalhadores. Grandes distâncias acarretam perda de tempo na produção e esforço extra.
      </Text>

      <Separator weight={4} />

      <Text size="small">
        <Bold size="small">Avaliação: </Bold> 100% <br />
        <Bold size="small">Melhorias: </Bold> Monitorar as possíveis melhorias em caso de modificação de arranjo físico.
      </Text>

      <Separator weight={4} />

      <Image src={imagemOrganizacao} maxWidth="350px" maxHeight="450px" ignoreFixedSize />

      <Separator weight={8} />

      <Text bold size="small">
        CONCLUSÃO / AÇÃO DE ERGONOMIA
      </Text>

      <ul>
        <li>
          <Text underlineColor={COLORS.lightGreen}>
            A organização do trabalho é adequada de acordo com a natureza do trabalho a ser executado. São respeitadas
            as características psicofisiológicas dos trabalhadores, de modo a proporcionar adequadas condições de
            trabalho, atendendo as exigências mínimas requisitadas pela Norma Regulamentadora n°17.
          </Text>
        </li>
      </ul>

      <Separator weight={8} />
    </View>
  )
}

export default FurnitureSection
